// Client for our own /api/vdatum/* proxy (see services/ais-api/src/server.js), which forwards
// to NOAA's public point-conversion service at vdatum.noaa.gov/vdatumweb/api/convert. We proxy it
// because NOAA's endpoint sends no Access-Control-Allow-Origin header.

async function fetchJson(url, init) {
  const res = await fetch(url, init);
  const data = await res.json().catch(() => null);
  if (!res.ok || !data?.ok) throw new Error(data?.error || `HTTP ${res.status}`);
  return data;
}

export function createVdatumClient() {
  async function convertPoint(settings) {
    const q = new URLSearchParams();
    Object.entries(settings || {}).forEach(([k, v]) => {
      if (v == null || v === "") return;
      q.set(k, String(v));
    });
    const data = await fetchJson(`/api/vdatum/convert?${q.toString()}`);
    return data;
  }

  async function convertBatch(rows, settings) {
    const data = await fetchJson("/api/vdatum/batch-convert", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ rows, settings: settings || {} }),
    });
    return data.results || [];
  }

  return { convertPoint, convertBatch };
}

// NOAA's documented region codes (https://vdatum.noaa.gov/docs/services.html) with a plain-
// language label, plus a rough lon/lat bounding box used only to suggest a default -- the user
// can always override it.
export const VDATUM_REGIONS = [
  { code: "contiguous", label: "Contiguous US", bbox: [-125, 24, -66, 49] },
  { code: "chesapeak_delaware", label: "Chesapeake / Delaware Bay", bbox: [-77.5, 36.5, -74.5, 40.5] },
  { code: "westcoast", label: "US West Coast (offshore)", bbox: [-128, 30, -116, 49] },
  { code: "wgom", label: "Western Gulf of Mexico", bbox: [-98, 25.5, -90, 30.5] },
  { code: "ak", label: "Alaska", bbox: [-180, 51, -129, 72] },
  { code: "seak", label: "Southeast Alaska (tidal)", bbox: [-140, 54, -129, 60] },
  { code: "hi", label: "Hawaii", bbox: [-161, 18, -154, 23] },
  { code: "prvi", label: "Puerto Rico / US Virgin Islands", bbox: [-68, 17, -64, 19] },
  { code: "as", label: "American Samoa", bbox: [-171.5, -14.5, -168, -13.5] },
  { code: "gcnmi", label: "Guam / CNMI", bbox: [144, 13, 146.5, 21] },
  { code: "spi", label: "St. Paul Island, AK", bbox: [-170.5, 57, -169.5, 57.3] },
  { code: "sgi", label: "St. George Island, AK", bbox: [-170.5, 56.4, -169.3, 56.7] },
  { code: "sli", label: "St. Lawrence Island, AK", bbox: [-172.7, 63, -169.4, 63.8] },
];

export function suggestRegion(lat, lon) {
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return "contiguous";
  const hit = VDATUM_REGIONS.find(({ bbox: [w, s, e, n] }) => lon >= w && lon <= e && lat >= s && lat <= n);
  return hit?.code || "contiguous";
}

const TIDAL_V_FRAMES = new Set(["MLLW", "MLW", "MTL", "MSL", "LMSL", "DTL", "MHW", "MHHW", "LWD"]);

// NOAA's VDatum server rejects a tidal conversion in some regions unless the horizontal frame on
// that side is a specific one (confirmed against the live service: "westcoast" requires IGS14 for
// a tidal vertical frame -- https://vdatum.noaa.gov/docs/services.html doesn't spell this out, the
// service's own error message does). Called whenever the region or a vertical frame changes, so
// the UI doesn't make the user discover this by hitting NOAA's error.
export function regionRequiredHFrame(region, vFrame) {
  if (region === "westcoast" && TIDAL_V_FRAMES.has(vFrame)) return "IGS14";
  return null;
}

// Horizontal/vertical frames worth exposing in the UI (NOAA's full lists are much longer).
export const VDATUM_H_FRAMES = ["NAD83_2011", "NAD83_1986", "NAD83_HARN", "WGS84_G1674", "ITRF2014", "IGS14"];
export const VDATUM_V_FRAMES = [
  { code: "NAVD88", label: "NAVD88 (orthometric)" },
  { code: "NGVD29", label: "NGVD29 (orthometric)" },
  { code: "MHHW", label: "MHHW -- Mean Higher High Water" },
  { code: "MHW", label: "MHW -- Mean High Water" },
  { code: "MTL", label: "MTL -- Mean Tide Level" },
  { code: "MSL", label: "MSL -- Mean Sea Level" },
  { code: "DTL", label: "DTL -- Diurnal Tide Level" },
  { code: "MLW", label: "MLW -- Mean Low Water" },
  { code: "MLLW", label: "MLLW -- Mean Lower Low Water (chart datum)" },
  { code: "LWD", label: "LWD -- Local Water Datum" },
  { code: "GEOID18", label: "GEOID18 (ellipsoid height)" },
];
